import { useEffect, useState } from 'react';
import { SketchPicker } from 'react-color';
import { useTranslation } from 'react-i18next';
import { Box, InputAdornment, Popover } from '@mui/material';

import TextInput, { TextInputProps } from '~/components/ui/TextInput';
import { getIsValidHexColor } from '~/utils/validation/color';

type ColorInputPickerProps = {
  value?: string;
  defaultValue?: string;
  onChange: (newColor: string) => void;
  withInternalOnChange?: boolean; // TODO temporary bad solution, need to use ColorInputPickerV2
  onClosePicker?: (newColor: string) => void;
};

export const ColorInputPicker = ({
  onChange,
  defaultValue,
  value,
  withInternalOnChange,
  onClosePicker,
  ...props
}: ColorInputPickerProps & Omit<TextInputProps, 'onChange'>) => {
  const { t } = useTranslation();

  const [color, setColor] = useState(defaultValue || value || '#000000');
  const [anchorEl, setAnchorEl] = useState(null);

  const open = Boolean(anchorEl);
  const id = open ? 'color-popover' : undefined;

  useEffect(() => {
    if (!open && ((defaultValue && defaultValue !== color) || (value && value !== color))) {
      if (withInternalOnChange && onClosePicker) {
        onClosePicker(value ?? color);
      } else {
        onChange(color);
      }
    }
  }, [open]);

  const colorValue = withInternalOnChange ? value : color;
  const isValidColor = getIsValidHexColor(colorValue);

  const handleChangeColor = (newColor: string) => {
    if (withInternalOnChange) {
      onChange(newColor);
    } else {
      setColor(newColor);
    }
  };

  return (
    <TextInput
      {...props}
      value={withInternalOnChange ? value : color}
      onChange={(e) => {
        handleChangeColor(e.target.value);
      }}
      onBlur={() => {
        if (onClosePicker) {
          onClosePicker(value ?? color);
        }
      }}
      error={!isValidColor}
      helperText={!isValidColor && t('errors:invalidHexCode')}
      InputProps={{
        endAdornment: (
          <>
            <InputAdornment
              position="end"
              onClick={(event) => setAnchorEl(event.currentTarget)}
              sx={{ cursor: 'pointer' }}
            >
              <Box
                style={{ backgroundColor: colorValue }}
                sx={{
                  width: '20px',
                  height: '20px',
                  borderRadius: '2px',
                  border: colorValue?.toLowerCase() === '#ffffff' ? `1px solid #000` : undefined,
                }}
              />
            </InputAdornment>
            <Popover
              id={`${id}${colorValue}`}
              open={open}
              anchorEl={anchorEl}
              onClose={() => setAnchorEl(null)}
              anchorOrigin={{
                vertical: 'bottom',
                horizontal: 'right',
              }}
              transformOrigin={{
                vertical: -20,
                horizontal: 'right',
              }}
            >
              <Box width={220}>
                <SketchPicker
                  color={colorValue}
                  onChange={({ hex }) => {
                    handleChangeColor(hex);
                  }}
                  onChangeComplete={(newColor) => {
                    handleChangeColor(newColor.hex);
                  }}
                />
              </Box>
            </Popover>
          </>
        ),
      }}
    />
  );
};
